import {
  ChevronLeft,
  ViewColumn as ColumnIcon,
  Storage as DatabaseIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  TableChart as TableIcon,
  WorkspacesTwoTone,
  BusinessCenter,
} from "@mui/icons-material";
import {
  Box,
  Collapse,
  List,
  Divider,
  Typography,
  Tooltip,
   Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useEffect, useRef, useState, memo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ROUTES } from "../../constants";
import { useDatabase } from "../../contexts/DatabaseContext";
import { useAuth } from "../../hooks/useAuth";
import { useRbac } from "../../hooks/RbacContext";
import { useDbConnection } from "../../hooks/useDbConnection";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  CollapseButton,
  ColumnList,
  PrimaryMenuItem,
  PrimarySidebar,
  ProfileCard,
  ProfileCardContent,
  ProfileName,
  ProfileRole,
  ScrollableContent,
  SecondarySidebar,
  SidebarContainer,
  TableHeader,
  TableTitle,
  TreeItem,
  TreeItemContent,
  TreeItemIcon,
  TreeItemLabel,
} from "./Sidebar.styled";
import AddIcon from "@mui/icons-material/Add";
import Button from "@mui/material/Button";
import SettingsIcon from "@mui/icons-material/Settings";
import HomeIcon from "@mui/icons-material/Home";
import PushPinIcon from "@mui/icons-material/PushPin";
import { Assignment } from "@mui/icons-material";
import { ManageAccounts } from "@mui/icons-material";
import { RateReview } from "@mui/icons-material";
import { StorageHelper } from "../../utils/storageHelper";
import { cleanTableName } from "../../utils/tableNameHelper";

const TreeNode = memo(({
  node,
  level = 0,
  ICON_SIZE,
  TABLE_ICON_SIZE,
  GRAY_07,
  FONT_SIZE,
  sx,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const hasChildren = node.children && node.children.length > 0;
  const isTable = node.type === "table" || node.type === "Table";
  const isColumn = node.type === "column";
  const isDatabase = node.type === "database";
  const itemRef = useRef(null);

  const getNodeIcon = (n) => {
    if (isColumn)
      return (
        <ColumnIcon
          fontSize="small"
          sx={{ fontSize: ICON_SIZE, color: GRAY_07 }}
        />
      );
    if (isTable)
      return (
        <TableIcon
          fontSize="small"
          sx={{
            fontSize: TABLE_ICON_SIZE,
            color: GRAY_07,
            minWidth: TABLE_ICON_SIZE,
            minHeight: TABLE_ICON_SIZE,
            width: TABLE_ICON_SIZE,
            height: TABLE_ICON_SIZE,
          }}
          style={{
            fontSize: TABLE_ICON_SIZE,
            minWidth: TABLE_ICON_SIZE,
            minHeight: TABLE_ICON_SIZE,
            width: TABLE_ICON_SIZE,
            height: TABLE_ICON_SIZE,
          }}
        />
      );
    return (
      <DatabaseIcon
        fontSize="small"
        sx={{ fontSize: ICON_SIZE, color: GRAY_07 }}
      />
    );
  };

  return (
    <TreeItem sx={sx}>
      <TreeItemContent
        ref={itemRef}
        onClick={(e) => {
          e.stopPropagation();
          hasChildren && setExpanded(!expanded);
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        iscolumn={isColumn}
        istable={isTable}
        level={level}
        draggable={isColumn}
        data-iscolumn={isColumn}
        data-istable={isTable}
        sx={{
          paddingLeft: `${level * 12 + (isTable ? 6 : 4)}px`,
        }}
        onDragStart={(e) => {
          if (isColumn) {
            const isPostgres = node.tableName && node.tableName.includes("__");
            const databaseType = isPostgres ? "postgres" : "snowflake";
            e.dataTransfer.setData(
              "text/plain",
              JSON.stringify({
                dbname: node.tableName || node.dbname || "",
                name: node.name,
                type: node.dataType || "",
                databaseType,
                __sourceZone: "sidebar",
              })
            );
            if (itemRef.current) {
              e.dataTransfer.setDragImage(
                itemRef.current,
                e.clientX - itemRef.current.getBoundingClientRect().left,
                e.clientY - itemRef.current.getBoundingClientRect().top
              );
            }
            e.dataTransfer.effectAllowed = "copy";
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = "copy";
        }}
        onDrop={(e) => {
          e.preventDefault();
          const data = JSON.parse(e.dataTransfer.getData("text/plain"));
          console.log("Column dropped:", data);
        }}
      >
        <TreeItemIcon
          iscolumn={isColumn}
          istable={isTable}
          sx={{ 
            minWidth: isTable ? "20px" : "16px", 
            color: GRAY_07,
            display: "flex",
            alignItems: "center",
            gap: isTable ? "8px" : "5px",
          }}
          data-iscolumn={isColumn}
          data-istable={isTable}
        >
          {hasChildren && (
            <KeyboardArrowDownIcon
              fontSize="small"
              sx={{ 
                fontSize: isTable ? "14px" : "12px", 
                color: expanded ? "rgb(44, 52, 123)" : "#9CA3AF",
                transform: expanded ? "rotate(0deg)" : "rotate(-90deg)",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                flexShrink: 0,
              }}
            />
          )}
          {!isTable && getNodeIcon(node)}
        </TreeItemIcon>
        <Tooltip title={node.name} placement="right" arrow>
          <TreeItemLabel
            noWrap
            iscolumn={isColumn}
            istable={isTable}
            data-iscolumn={isColumn}
            data-istable={isTable}
          >
            {node.name}
            {isTable && hasChildren && (
              <Box
                component="span"
                sx={{
                  ml: 0.75,
                  fontSize: "10px",
                  color: "#9CA3AF",
                  fontWeight: 500,
                  fontFeatureSettings: '"tnum"',
                }}
              >
                ({node.children.length})
              </Box>
            )}
          </TreeItemLabel>
        </Tooltip>
      </TreeItemContent>

      {hasChildren && (
        <Collapse 
          in={expanded} 
          timeout={300}
          easing={{ enter: "cubic-bezier(0.4, 0, 0.2, 1)", exit: "cubic-bezier(0.4, 0, 0.2, 1)" }}
        >
          {isTable && (
            <TableHeader>
              <TableTitle
                sx={{ fontSize: "9.5px", fontWeight: 500, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.25px", fontFeatureSettings: '"tnum"' }}
              >
                Columns ({node.children.length})
              </TableTitle>
            </TableHeader>
          )}
          <ColumnList>
            <List 
              component="div" 
              disablePadding 
              sx={{ 
                paddingLeft: isTable ? "12px" : "6px",
                paddingTop: isTable ? "4px" : "0px",
                paddingBottom: isTable ? "8px" : "0px",
              }}
            >
              {node.children.map((child, index) => (
                <TreeNode
                  key={`${node.id ?? node.name}-${child.columnName ?? child.tableName ?? child.id ?? index}-${index}`}
                  node={child}
                  level={level + 1}
                  ICON_SIZE={ICON_SIZE}
                  TABLE_ICON_SIZE={TABLE_ICON_SIZE}
                  GRAY_07={GRAY_07}
                  FONT_SIZE={FONT_SIZE}
                  sx={{
                    animation: expanded ? `fadeIn 0.3s ease ${index * 0.03}s both` : "none",
                    "@keyframes fadeIn": {
                      from: {
                        opacity: 0,
                        transform: "translateY(-4px)",
                      },
                      to: {
                        opacity: 1,
                        transform: "translateY(0)",
                      },
                    },
                  }}
                />
              ))}
            </List>
          </ColumnList>
        </Collapse>
      )}
    </TreeItem>
  );
});
TreeNode.displayName = "TreeNode";

const DatabaseTree = ({ ICON_SIZE, TABLE_ICON_SIZE, GRAY_07, FONT_SIZE }) => {
  const { databaseState } = useDatabase();
  const [isLoading, setIsLoading] = useState(true);
  const [tablesData, setTablesData] = useState(null);
  const selectedUserDbId = localStorage.getItem("selected_user_db_id");

  useEffect(() => {
    let timeout;

    // Always render the expand animation first; defer actual work a tick.
    timeout = window.setTimeout(() => {
      // Hydrate from localStorage only; do not touch global context here
      const raw = localStorage.getItem("snowflake_tables_data");
      if (!raw || !selectedUserDbId) {
        setTablesData([]);
        setIsLoading(false);
        return;
      }
      try {
        const parsed = JSON.parse(raw);
        setTablesData(Array.isArray(parsed) ? parsed : []);
      } catch {
        setTablesData([]);
      } finally {
        setIsLoading(false);
      }
    }, 0);

    return () => {
      if (timeout) {
        window.clearTimeout(timeout);
      }
    };
  }, [selectedUserDbId]);

  if (isLoading) {
    return (
      <Box sx={{ p: 2, textAlign: "center", color: "text.secondary", fontSize: 11 }}>
        Loading databases...
      </Box>
    );
  }

  if (!Array.isArray(tablesData) || tablesData.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: "center", color: "text.secondary", fontSize: 11 }}>
        No database connected
      </Box>
    );
  }

  return (
    <Box sx={{ px: 0.5, py: 0.5 }}>
      {tablesData.map((db) => (
        <Box key={db.id ?? db.name}>
          {/* Tables List */}
          <List component="nav" sx={{ width: "100%", pl: 0, py: 0 }}>
            {Array.isArray(db.children) && db.children.length > 0 ? (
              db.children.map((table, tIdx) => (
                <TreeNode
                  key={`${db.id ?? db.name}-${table.id ?? table.name ?? tIdx}`}
                  node={{
                    id: table.id,
                    name: table.name,
                    type: "table",
                    children: Array.isArray(table.children)
                      ? table.children.map((column) => ({
                          id: column.id,
                          name: column.name,
                          type: "column",
                          dataType: column.textDatatype,
                          tableName: column.tableName,
                        }))
                      : [],
                  }}
                  ICON_SIZE={ICON_SIZE}
                  TABLE_ICON_SIZE={TABLE_ICON_SIZE}
                  GRAY_07={GRAY_07}
                  FONT_SIZE={FONT_SIZE}
                />
              ))
            ) : (
              <Box sx={{ px: 1, py: 0.5, fontSize: 10, color: "#9CA3AF" }}>
                No tables available
              </Box>
            )}
          </List>
        </Box>
      ))}
    </Box>
  );
};

const Sidebar = (props) => {
  const theme = useTheme();
  const ICON_SIZE = theme.sidebar?.iconSize || "12px";
  const TABLE_ICON_SIZE = theme.sidebar?.tableIconSize || "12px";
  const GRAY_07 = theme.sidebar?.gray07;
  const FONT_SIZE = theme.sidebar?.fontSize || "13px";

  const { logout } = useAuth();
  const { checkAccess, permissions, loading } = useRbac();
  const can = (module, action = "view") =>
    !loading ? checkAccess(module, action) : false;

  const navigate = useNavigate();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [userDetails, setUserDetails] = useState({});
  const [showProfileCard, setShowProfileCard] = useState(false);
  const profileCardRef = useRef(null);
  const profileButtonRef = useRef(null);
  const [hasSelectedDb, setHasSelectedDb] = useState(
    Boolean(localStorage.getItem("selected_user_db_id"))
  );
  const { getPostgresConnectionDetails } = useDbConnection();
  const { databaseState } = useDatabase();
  const { user } = useAuth();
  const [postgresTables, setPostgresTables] = useState([]);
  const [sharedTables, setSharedTables] = useState([]);
  const [loadingPostgres, setLoadingPostgres] = useState(false);
  const [loadingSharedTables, setLoadingSharedTables] = useState(false);
  // Which sidebar accordion section is expanded: 'db' | 'saved' | 'shared' | null
  const [expandedSection, setExpandedSection] = useState("db");
  // Mount the heavy DatabaseTree slightly after expand to avoid UI lag
  const [showDbTree, setShowDbTree] = useState(false);

  useEffect(() => {
    const userLS = localStorage.getItem("userDetails");
    if (userLS) {
      setUserDetails(JSON.parse(userLS));
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        profileCardRef.current &&
        !profileCardRef.current.contains(event.target) &&
        !profileButtonRef.current.contains(event.target)
      ) {
        setShowProfileCard(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleDbChange = () => {
      setHasSelectedDb(Boolean(localStorage.getItem("selected_user_db_id")));
    };
    window.addEventListener("selected_user_db_id_changed", handleDbChange);
    return () =>
      window.removeEventListener("selected_user_db_id_changed", handleDbChange);
  }, []);

  // Collapse automatically on certain pages (including Home/Dashboard so sidebar stays closed on home)
  useEffect(() => {
    if (
      location.pathname === ROUTES.DASHBOARD ||
      location.pathname === ROUTES.DB_DIAGRAM ||
      location.pathname === ROUTES.HOME ||
      location.pathname === ROUTES.DASHBOARD ||
      location.pathname === ROUTES.TEXT_TO_SQL ||
      location.pathname === ROUTES.SAVED_LIST ||
      location.pathname.startsWith(ROUTES.ASK_FLASHDICE) ||
      location.pathname === ROUTES.FILE_UPLOAD ||
      location.pathname === ROUTES.USERMANAGEMENT ||
      location.pathname === ROUTES.ROLES ||
      location.pathname === ROUTES.REVIEW_SQL ||
      location.pathname === ROUTES.TABLE_ACCESS ||
      location.pathname === ROUTES.BUSINESS_DEFINITION ||
      location.pathname.includes(
        ROUTES.COLLABORATION.replace("/:channel_code", "")
      )
    ) {
      setIsCollapsed(true);
      props.onCollapse?.(true);
    } else {
      setIsCollapsed(false);
      props.onCollapse?.(false);
    }
  }, [location.pathname, props.onCollapse]);

  // Load saved tables for the landing/dashboard only
  useEffect(() => {
    const fetchPostgresTables = async () => {
      if (!user?.userDetails?.user_id) return;
      if (
        location.pathname !== ROUTES.DASHBOARD &&
        location.pathname !== ROUTES.HOME &&
        location.pathname !== "/"
      ) {
        return;
      }
      setLoadingPostgres(true);
      setLoadingSharedTables(true);
      const result = await getPostgresConnectionDetails(
        user.userDetails.user_id
      );
      setLoadingPostgres(false);
      setLoadingSharedTables(false);
      if (result.success && result.data?.tablesData) {
        let tables = result.data.tablesData;
        if (typeof tables === "string") {
          try {
            tables = JSON.parse(tables);
          } catch {}
        }
        setPostgresTables(Array.isArray(tables) ? tables : []);
        StorageHelper.set("postgres_tables_data", Array.isArray(tables) ? tables : []);
      } else {
        setPostgresTables([]);
        StorageHelper.remove("postgres_tables_data");
      }
      // Handle shared tables
      if (result.success && result.data?.sharedTablesData) {
        let shared = result.data.sharedTablesData;
        if (typeof shared === "string") {
          try {
            shared = JSON.parse(shared);
          } catch { }
        }
        setSharedTables(Array.isArray(shared) ? shared : []);
        StorageHelper.set("postgres_shared_tables_data", Array.isArray(shared) ? shared : []);
      } else {
        setSharedTables([]);
        StorageHelper.remove("postgres_shared_tables_data");
      }
    };
    fetchPostgresTables();
  }, [user, getPostgresConnectionDetails, location.pathname]);

  // Defer mounting DatabaseTree until shortly after the DB accordion is expanded
  useEffect(() => {
    let timeoutId;
    if (expandedSection === "db") {
      // Let the accordion expand first, then mount the heavy tree
      timeoutId = window.setTimeout(() => {
        setShowDbTree(true);
      }, 50);
    } else {
      setShowDbTree(false);
    }
    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [expandedSection]);

  // Build menu (RBAC keys match your roleOptions / GetRbac)
  const menuItems = [
    {
      title: "ANALYZE",
      items: [
        {
          name: "Dashboard",
          icon: <HomeIcon />,
          path: ROUTES.DASHBOARD,
          module: "dashboard",
          action: "view",
        },
        {
          name: "Saved Queries",
          icon: <PushPinIcon />,
          path: ROUTES.SAVED_LIST,
          module: "savedQueries",
          action: "view",
        },
        {
          name: "Ask FlashDice",
          icon: <AutoAwesomeIcon />,
          path: ROUTES.ASK_FLASHDICE,
          module: "askFlashdice",
          action: "view",
        },
      ],
    },
    {
      title: "COLLABORATE",
      items: [
        {
          name: "Collaboration",
          icon: <WorkspacesTwoTone />,
          path: ROUTES.COLLABORATION.replace(":channel_code", "CHANNEL-CODE"),
          module: "collaboration",
          action: "view",
        },
      ],
    },
    {
      title: "DATA",
      items: [
        {
          name: "File Upload",
          icon: <AddIcon />,
          path: ROUTES.FILE_UPLOAD,
          module: "fileUpload",
          action: "view",
        },
      ],
    },
    {
      title: "ADMIN",
      items: [
        {
          name: "Settings",
          icon: <SettingsIcon />,
          // No module at parent; sub-items are RBAC-gated
          subItem: [
            {
              name: "Roles",
              icon: <Assignment />,
              path: ROUTES.ROLES,
              module: "role",
              action: "view",
            },
            {
              name: "User Management",
              icon: <ManageAccounts />,
              path: ROUTES.USERMANAGEMENT,
              module: "userManagement",
              action: "view",
            },
            {
              name: "Review SQL",
              icon: <RateReview />,
              path: ROUTES.REVIEW_SQL,
              module: "reviewSql",
              action: "view",
            },
            {
              name: "Business Definition",
              icon: <ColumnIcon />,
              path: ROUTES.BUSINESS_DEFINITION,
              module: "businessDefinition",
              action: "view",
            },
          ],
        },
      ],
    },
  ];

  // Filter menu by RBAC (same behavior as your working code)
  const filteredMenuItems = menuItems
    .map((section) => ({
      ...section,
      items: section.items
        .map((item) => ({
          ...item,
          subItem: item.subItem?.filter(
            (subitem) => !subitem.module || can(subitem.module, subitem.action)
          ),
        }))
        .filter(
          (item) =>
            !item.module ||
            can(item.module, item.action) ||
            (item.subItem && item.subItem.length > 0)
        ),
    }))
    .filter((section) => section.items.length > 0);

  const handleNavigation = (path) => {
    navigate(path);
    // Always close sidebar when navigating to Home/Dashboard
    if (path === ROUTES.HOME || path === ROUTES.DASHBOARD) {
      setIsCollapsed(true);
      props.onCollapse?.(true);
    } else if (path === ROUTES.TIMESHEET) {
      const newCollapsedState = true;
      setIsCollapsed(newCollapsedState);
      props.onCollapse?.(newCollapsedState);
    }
  };

  const handleCollapse = () => {
    if (
      location.pathname === ROUTES.DB_DIAGRAM ||
      location.pathname === ROUTES.ASK_FLASHDICE ||
      location.pathname === ROUTES.HOME ||
      location.pathname === ROUTES.SAVED_LIST ||
      location.pathname === ROUTES.FILE_UPLOAD ||
      location.pathname === ROUTES.USERMANAGEMENT ||
      location.pathname === ROUTES.ROLES ||
      location.pathname === ROUTES.REVIEW_SQL ||
      location.pathname === ROUTES.TABLE_ACCESS
    ) {
      return;
    }
    const newCollapsedState = !isCollapsed;

    // When collapsing the sidebar, also close all inner accordions.
    if (newCollapsedState) {
      setExpandedSection(null);
      setShowDbTree(false);
    }

    setIsCollapsed(newCollapsedState);
    props.onCollapse?.(newCollapsedState);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const PrimaryIcon = ({ show, title, active, onClick, children }) =>
    show ? (
      <Tooltip title={title} placement="right" arrow>
        <PrimaryMenuItem
          onClick={onClick}
          active={active}
          sx={{
            width: { xs: 24, sm: 28, md: 32 },
            height: { xs: 24, sm: 28, md: 32 },
            minWidth: { xs: 24, sm: 28, md: 32 },
            minHeight: { xs: 24, sm: 28, md: 32 },
          }}
        >
          {children}
        </PrimaryMenuItem>
      </Tooltip>
    ) : null;

    // Flatten tables from all databases into a single list
    const flattenTables = (tables) => {
      const flattened = [];
      tables.forEach((db) => {
        if (db.children && Array.isArray(db.children)) {
          db.children.forEach((table) => {
            flattened.push({
              ...table,
              dbName: db.name, // Keep db name for reference if needed
            });
          });
        }
      });
      return flattened;
    };

    const renderTreeList = (tables, loading, emptyMsg, sectionKey = "list") => {
      const flattenedTables = tables.length > 0 ? flattenTables(tables) : [];
      
      return (
        <>
          {loading ? (
            <Box 
              sx={{ 
                p: 2, 
                textAlign: "center", 
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  border: "2px solid rgba(44, 52, 123, 0.2)",
                  borderTopColor: "rgb(44, 52, 123)",
                  borderRadius: "50%",
                  animation: "spin 0.8s linear infinite",
                  "@keyframes spin": {
                    to: { transform: "rotate(360deg)" },
                  },
                }}
              />
              <Typography sx={{ fontSize: 11, color: "#6b7280", fontWeight: 500 }}>
                Loading...
              </Typography>
            </Box>
          ) : flattenedTables.length === 0 ? (
            <Box 
              sx={{ 
                p: 2, 
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.75,
              }}
            >
              <TableIcon sx={{ fontSize: 24, color: "#d1d5db", opacity: 0.5 }} />
              <Typography sx={{ fontSize: 11, color: "#9ca3af", fontWeight: 400 }}>
                {emptyMsg}
              </Typography>
            </Box>
          ) : (
            <List component="nav" sx={{ width: "100%", py: 0.125, px: 0, boxSizing: "border-box", overflow: "hidden" }}>
              {flattenedTables.map((table, tableIndex) => {
                const cleanName = cleanTableName(table.name);
                const uniqueKey = `${sectionKey}-${table.dbName ?? "db"}-${table.id ?? table.name ?? tableIndex}-${tableIndex}`;
                return (
                  <TreeNode
                    key={uniqueKey}
                    node={{
                      id: table.id,
                      name: cleanName,
                      type: table.type || "table",
                      children:
                        table.children?.map((column) => ({
                          id: column.id,
                          name: column.name,
                          type: column.type || "column",
                          dataType: column.textDatatype,
                          tableName: column.tableName,
                        })) || [],
                    }}
                    ICON_SIZE={ICON_SIZE}
                    TABLE_ICON_SIZE={TABLE_ICON_SIZE}
                    GRAY_07={GRAY_07}
                    FONT_SIZE={FONT_SIZE}
                    sx={{
                      mb: tableIndex < flattenedTables.length - 1 ? 0.125 : 0,
                      animation: `fadeInSlide 0.2s ease ${tableIndex * 0.01}s both`,
                      "@keyframes fadeInSlide": {
                        from: {
                          opacity: 0,
                          transform: "translateY(-1px)",
                        },
                        to: {
                          opacity: 1,
                          transform: "translateY(0)",
                        },
                      },
                    }}
                  />
                );
              })}
            </List>
          )}
        </>
      );
    };

  return (
    <SidebarContainer>
      <PrimarySidebar>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: { xs: "6px", sm: "10px", md: "14px" },
            alignItems: "center",
          }}
        >
          {/* RBAC-gated primary icons */}
          <PrimaryIcon
            show={can("dashboard", "view")}
            title="Home"
            active={
              location.pathname === ROUTES.DASHBOARD ||
              location.pathname === ROUTES.HOME
            }
            onClick={() => handleNavigation(ROUTES.DASHBOARD)}
          >
            <HomeIcon
              sx={{ color: "inherit", fontSize: { xs: 16, sm: 20, md: 22 } }}
            />
          </PrimaryIcon>

          <PrimaryIcon
            show={can("tableAccess", "view")}
            title="Table Access"
            active={location.pathname === ROUTES.TABLE_ACCESS}
            onClick={() => handleNavigation(ROUTES.TABLE_ACCESS)}
          >
            <TableIcon
              sx={{ color: "inherit", fontSize: { xs: 14, sm: 16, md: 18 } }}
            />
          </PrimaryIcon>

          <PrimaryIcon
            show={can("fileUpload", "view")}
            title="Add New Table"
            active={location.pathname === ROUTES.FILE_UPLOAD}
            onClick={() => handleNavigation(ROUTES.FILE_UPLOAD)}
          >
            <AddIcon
              sx={{ color: "inherit", fontSize: { xs: 16, sm: 20, md: 22 } }}
            />
          </PrimaryIcon>

          <PrimaryIcon
            show={can("collaboration", "view")}
            title="Collaboration"
            active={location.pathname.includes(
              ROUTES.COLLABORATION.replace(":channel_code", "")
            )}
            onClick={() => {
              setIsCollapsed(true);
              props.onCollapse?.(true);
              navigate(
                ROUTES.COLLABORATION.replace(":channel_code", "CHANNEL-CODE")
              );
            }}
          >
            <WorkspacesTwoTone
              sx={{ color: "inherit", fontSize: { xs: 14, sm: 16, md: 18 } }}
            />
          </PrimaryIcon>

          <PrimaryIcon
            show={can("savedQueries", "view")}
            title="Saved Queries"
            active={location.pathname === ROUTES.SAVED_LIST}
            onClick={() => handleNavigation(ROUTES.SAVED_LIST)}
          >
            <PushPinIcon
              sx={{ color: "inherit", fontSize: { xs: 14, sm: 16, md: 18 } }}
            />
          </PrimaryIcon>

          <PrimaryIcon
            show={can("askFlashdice", "view")}
            title="Ask FlashDice"
            active={location.pathname.startsWith(ROUTES.ASK_FLASHDICE)}
            onClick={() => handleNavigation(ROUTES.ASK_FLASHDICE)}
          >
            <AutoAwesomeIcon
              sx={{ color: "inherit", fontSize: { xs: 14, sm: 16, md: 18 } }}
            />
          </PrimaryIcon>

          <PrimaryIcon
            show={can("role", "view")}
            title="Role"
            active={location.pathname === ROUTES.ROLES}
            onClick={() => handleNavigation(ROUTES.ROLES)}
          >
            <Assignment
              sx={{ color: "inherit", fontSize: { xs: 16, sm: 20, md: 22 } }}
            />
          </PrimaryIcon>

          <PrimaryIcon
            show={can("userManagement", "view")}
            title="User Management"
            active={location.pathname === ROUTES.USERMANAGEMENT}
            onClick={() => handleNavigation(ROUTES.USERMANAGEMENT)}
          >
            <ManageAccounts
              sx={{ color: "inherit", fontSize: { xs: 16, sm: 20, md: 22 } }}
            />
          </PrimaryIcon>

          <PrimaryIcon
            show={can("reviewSql", "view")}
            title="Review SQL"
            active={location.pathname === ROUTES.REVIEW_SQL}
            onClick={() => handleNavigation(ROUTES.REVIEW_SQL)}
          >
            <RateReview
              sx={{ color: "inherit", fontSize: { xs: 16, sm: 20, md: 22 } }}
            />
          </PrimaryIcon>

          <PrimaryIcon
            show={can("businessDefinition", "view")}
            title="Business Definition"
            active={location.pathname === ROUTES.BUSINESS_DEFINITION}
            onClick={() => handleNavigation(ROUTES.BUSINESS_DEFINITION)}
          >
            <BusinessCenter
              sx={{ color: "inherit", fontSize: { xs: 16, sm: 20, md: 22 } }}
            />
          </PrimaryIcon>

          {(location.pathname === ROUTES.DASHBOARD) && (
            <Tooltip
              title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
              placement="right"
              arrow
            >
              <CollapseButton
                onClick={handleCollapse}
                collapsed={isCollapsed}
              >
                <ChevronLeft />
              </CollapseButton>
            </Tooltip>
          )}
        </Box>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        />

        <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <Tooltip title="Profile" placement="right" arrow>
            <PrimaryMenuItem
              ref={profileButtonRef}
              onClick={() => setShowProfileCard(!showProfileCard)}
            >
              <PersonIcon
                data-testid="PersonIcon"
                sx={{ color: "inherit", fontSize: "20px" }}
              />
            </PrimaryMenuItem>
          </Tooltip>

          <Tooltip title="Logout" placement="right" arrow>
            <PrimaryMenuItem onClick={handleLogout}>
              <LogoutIcon
                data-testid="LogoutIcon"
                sx={{ color: "inherit", fontSize: "20px" }}
              />
            </PrimaryMenuItem>
          </Tooltip>
        </Box>
      </PrimarySidebar>

      <ProfileCard
        ref={profileCardRef}
        className={showProfileCard ? "show" : ""}
      >
        <ProfileCardContent>
          <ProfileName>
            {userDetails?.first_name} {userDetails?.last_name}
          </ProfileName>
          <ProfileRole>
            {permissions?.globalRole?.role_name ||
              userDetails?.role_name ||
              "User"}
          </ProfileRole>
        </ProfileCardContent>
      </ProfileCard>

      <SecondarySidebar collapsed={isCollapsed}>
        {isCollapsed ? (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "8px 0",
              justifyContent: "center",
              background: "#fff",
            }}
          >
            <DatabaseIcon
              data-testid="database-icon"
              sx={{ fontSize: ICON_SIZE, color: "inherit", opacity: 0.8 }}
            />
          </Box>
        ) : (
          <>
            <ScrollableContent style={{ background: "#fff" }}>
              <Box sx={{ width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
                {/* Database Tables Section */}
              <Box sx={{ width: "100%", maxWidth: "100%", boxSizing: "border-box", px: 0.5, mb: 0.5 }}>
                <Accordion 
                  disableGutters 
                  expanded={expandedSection === "db"}
                  onChange={(_, isExpanded) =>
                    setExpandedSection(isExpanded ? "db" : null)
                  }
                    sx={{ 
                      boxShadow: "none",
                      "&:before": { display: "none" },
                      backgroundColor: "transparent",
                      mb: 0.5,
                      width: "100%",
                      maxWidth: "100%",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={
                        <ExpandMoreIcon 
                          sx={{ 
                            color: "rgb(44, 52, 123)",
                            fontSize: "16px",
                            transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                          }}
                        />
                      }
                      sx={{
                        backgroundColor: "#F8F9FA",
                        px: 0.875,
                        py: 0.5,
                        borderRadius: "5px",
                        mx: 0.5,
                        mb: 0.15,
                        minHeight: "32px",
                        border: "1px solid #E5E7EB",
                        width: "calc(100% - 8px)",
                        maxWidth: "100%",
                        boxSizing: "border-box",
                        transition: "all 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 0.5,
                        "&:hover": {
                          backgroundColor: "#F3F4F6",
                          borderColor: "#D1D5DB",
                        },
                        "&.Mui-expanded": {
                          backgroundColor: "#F3F4F6",
                          borderColor: "#D1D5DB",
                        },
                        "& .MuiAccordionSummary-expandIconWrapper": {
                          transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                        },
                        "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                          transform: "rotate(180deg)",
                        },
                        "& .MuiAccordionSummary-content": {
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          width: "100%",
                          pr: 0.5,
                          gap: 0.5,
                          margin: 0,
                          "&.Mui-expanded": {
                            margin: 0,
                          },
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
                        <DatabaseIcon sx={{ fontSize: 14, color: "rgb(44, 52, 123)", flexShrink: 0 }} />
                        <Typography sx={{ fontWeight: 600, color: "#374151", fontSize: 11, whiteSpace: "nowrap" }}>
                          {databaseState.tablesData && databaseState.tablesData.length > 0
                            ? databaseState.tablesData[0].name
                            : "Database Tables"}
                        </Typography>
                        {(() => {
                          const totalTables = databaseState.tablesData?.reduce((acc, db) => 
                            acc + (db.children?.length || 0), 0) || 0;
                          return totalTables > 0 ? (
                            <Box
                              sx={{
                                px: 0.5,
                                py: 0.125,
                                borderRadius: "6px",
                                backgroundColor: "#E0E7FF",
                                fontSize: "8.5px",
                                fontWeight: 600,
                                color: "rgb(44, 52, 123)",
                                minWidth: "14px",
                                textAlign: "center",
                                lineHeight: 1.2,
                                flexShrink: 0,
                              }}
                            >
                              {totalTables}
                            </Box>
                          ) : null;
                        })()}
                      </Box>
                      <Box
                        component="div"
                        role="button"
                        tabIndex={0}
                        aria-label="Select Database Tables"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (location.pathname !== ROUTES.DB_DIAGRAM) {
                            setIsCollapsed(true);
                            props.onCollapse?.(true);
                            navigate(ROUTES.DB_DIAGRAM);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            e.stopPropagation();
                            if (location.pathname !== ROUTES.DB_DIAGRAM) {
                              setIsCollapsed(true);
                              props.onCollapse?.(true);
                              navigate(ROUTES.DB_DIAGRAM);
                            }
                          }
                        }}
                        sx={{
                          minWidth: 0,
                          px: 0.75,
                          py: 0.3,
                          fontSize: 10,
                          fontWeight: 600,
                          borderRadius: "4px",
                          backgroundColor: "rgb(44, 52, 123)",
                          color: "#fff",
                          boxShadow: "none",
                          textTransform: "none",
                          transition: "all 0.15s ease",
                          flexShrink: 0,
                          cursor: location.pathname === ROUTES.DB_DIAGRAM ? "default" : "pointer",
                          "&:hover": location.pathname !== ROUTES.DB_DIAGRAM ? {
                            backgroundColor: "rgb(35, 42, 100)",
                            boxShadow: "0 1px 2px rgba(44, 52, 123, 0.25)",
                          } : {},
                          "&:active": location.pathname !== ROUTES.DB_DIAGRAM ? { transform: "scale(0.97)" } : {},
                          opacity: location.pathname === ROUTES.DB_DIAGRAM ? 0.6 : 1,
                          pointerEvents: location.pathname === ROUTES.DB_DIAGRAM ? "none" : "auto",
                        }}
                      >
                        Select
                      </Box>
                    </AccordionSummary>
                <AccordionDetails
                  sx={{
                    p: 0,
                    pt: 0.1,
                    pb: 0.1,
                    px: 0.5,
                    boxSizing: "border-box",
                    width: "100%",
                    maxWidth: "100%",
                  }}
                >
                  {expandedSection === "db" && (
                    <Box
                      sx={{
                        maxHeight: {
                          xs: "calc(100vh - 220px)",
                          sm: "calc(100vh - 190px)",
                          md: "calc(100vh - 175px)",
                          lg: "calc(100vh - 165px)",
                        },
                        overflowY: "auto",
                        pr: 0.5,
                      }}
                    >
                      {showDbTree ? (
                        <DatabaseTree
                          ICON_SIZE={ICON_SIZE}
                          TABLE_ICON_SIZE={TABLE_ICON_SIZE}
                          GRAY_07={GRAY_07}
                          FONT_SIZE={FONT_SIZE}
                        />
                      ) : (
                        <Box
                          sx={{
                            p: 2,
                            textAlign: "center",
                            color: "text.secondary",
                            fontSize: 11,
                          }}
                        >
                          Loading tables...
                        </Box>
                      )}
                    </Box>
                  )}
                </AccordionDetails>
                  </Accordion>
                </Box>

              {/* Saved Tables Section */}
              <Box sx={{ width: "100%", maxWidth: "100%", boxSizing: "border-box", px: 0.5 }}>
                <Accordion 
                  disableGutters 
                  expanded={expandedSection === "saved"}
                  onChange={(_, isExpanded) =>
                    setExpandedSection(isExpanded ? "saved" : null)
                  }
                  sx={{ 
                    boxShadow: "none",
                    "&:before": { display: "none" },
                    backgroundColor: "transparent",
                    mb: 0.5,
                    width: "100%",
                    maxWidth: "100%",
                  }}
                >
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreIcon 
                      sx={{ 
                        color: "rgb(44, 52, 123)",
                        fontSize: "14px",
                        transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      }}
                    />
                  }
                  sx={{
                    backgroundColor: "#F8F9FA",
                    px: 0.875,
                    py: 0.5,
                    borderRadius: "5px",
                    mx: 0.5,
                    mb: 0.15,
                    minHeight: "32px",
                    border: "1px solid #E5E7EB",
                    width: "calc(100% - 8px)",
                    maxWidth: "100%",
                    boxSizing: "border-box",
                    transition: "all 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 0.5,
                    "&:hover": {
                      backgroundColor: "#F3F4F6",
                      borderColor: "#D1D5DB",
                    },
                    "&.Mui-expanded": {
                      backgroundColor: "#F3F4F6",
                      borderColor: "#D1D5DB",
                    },
                    "& .MuiAccordionSummary-expandIconWrapper": {
                      transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    },
                    "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                      transform: "rotate(180deg)",
                    },
                    "& .MuiAccordionSummary-content": {
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      pr: 0.5,
                      gap: 0.5,
                      margin: 0,
                      "&.Mui-expanded": {
                        margin: 0,
                      },
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
                    <TableIcon sx={{ fontSize: 14, color: "rgb(44, 52, 123)", flexShrink: 0 }} />
                    <Typography sx={{ fontWeight: 600, color: "#374151", fontSize: 11, whiteSpace: "nowrap" }}>
                      Saved Tables
                    </Typography>
                    {(() => {
                      const totalTables = postgresTables.reduce((acc, db) => acc + (db.children?.length || 0), 0);
                      return totalTables > 0 ? (
                        <Box
                          sx={{
                            px: 0.5,
                            py: 0.125,
                            borderRadius: "6px",
                            backgroundColor: "#E0E7FF",
                            fontSize: "8.5px",
                            fontWeight: 600,
                            color: "rgb(44, 52, 123)",
                            minWidth: "14px",
                            textAlign: "center",
                            lineHeight: 1.2,
                            flexShrink: 0,
                          }}
                        >
                          {totalTables}
                        </Box>
                      ) : null;
                    })()}
                  </Box>
                    <Box
                      component="div"
                      role="button"
                      tabIndex={0}
                      aria-label="Select Saved Tables"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (location.pathname !== ROUTES.DB_DIAGRAM) {
                          setIsCollapsed(true);
                          props.onCollapse?.(true);
                          navigate(ROUTES.DB_DIAGRAM, { state: { source: "postgres" } });
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          e.stopPropagation();
                          if (location.pathname !== ROUTES.DB_DIAGRAM) {
                            setIsCollapsed(true);
                            props.onCollapse?.(true);
                            navigate(ROUTES.DB_DIAGRAM, { state: { source: "postgres" } });
                          }
                        }
                      }}
                      sx={{
                        minWidth: 0,
                        px: 0.75,
                        py: 0.3,
                        fontSize: 10,
                        fontWeight: 600,
                        borderRadius: "4px",
                        backgroundColor: "rgb(44, 52, 123)",
                        color: "#fff",
                        boxShadow: "none",
                        textTransform: "none",
                        transition: "all 0.15s ease",
                        flexShrink: 0,
                        cursor: location.pathname === ROUTES.DB_DIAGRAM ? "default" : "pointer",
                        "&:hover": location.pathname !== ROUTES.DB_DIAGRAM ? {
                          backgroundColor: "rgb(35, 42, 100)",
                          boxShadow: "0 1px 2px rgba(44, 52, 123, 0.25)",
                        } : {},
                        "&:active": location.pathname !== ROUTES.DB_DIAGRAM ? { transform: "scale(0.97)" } : {},
                        opacity: location.pathname === ROUTES.DB_DIAGRAM ? 0.6 : 1,
                        pointerEvents: location.pathname === ROUTES.DB_DIAGRAM ? "none" : "auto",
                      }}
                    >
                      Select
                    </Box>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    p: 0,
                    pt: 0.1,
                    pb: 0.1,
                    px: 0.5,
                    boxSizing: "border-box",
                    width: "100%",
                    maxWidth: "100%",
                  }}
                >
                  <Box
                    sx={{
                      maxHeight: {
                        xs: "calc(100vh - 220px)", 
                        sm: "calc(100vh - 190px)",
                        md: "calc(100vh - 175px)",
                        lg: "calc(100vh - 165px)",
                      },
                      overflowY: "auto",
                      pr: 0.5,
                    }}
                  >
                    {renderTreeList(postgresTables, loadingPostgres, "No saved tables", "saved")}
                  </Box>
                </AccordionDetails>
              </Accordion>
              </Box>

              {/* Shared Uploaded Files Section */}
              <Box sx={{ width: "100%", maxWidth: "100%", boxSizing: "border-box", px: 0.5 }}>
                <Accordion 
                  disableGutters 
                  expanded={expandedSection === "shared"}
                  onChange={(_, isExpanded) =>
                    setExpandedSection(isExpanded ? "shared" : null)
                  }
                  sx={{ 
                    boxShadow: "none",
                    "&:before": { display: "none" },
                    backgroundColor: "transparent",
                    width: "100%",
                    maxWidth: "100%",
                  }}
                >
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreIcon 
                      sx={{ 
                        color: "rgb(44, 52, 123)",
                        fontSize: "14px",
                        transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      }}
                    />
                  }
                  sx={{
                    backgroundColor: "#F8F9FA",
                    px: 0.875,
                    py: 0.5,
                    borderRadius: "5px",
                    mx: 0.5,
                    mb: 0.15,
                    minHeight: "32px",
                    border: "1px solid #E5E7EB",
                    width: "calc(100% - 8px)",
                    maxWidth: "100%",
                    boxSizing: "border-box",
                    transition: "all 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 0.5,
                    "&:hover": {
                      backgroundColor: "#F3F4F6",
                      borderColor: "#D1D5DB",
                    },
                    "&.Mui-expanded": {
                      backgroundColor: "#F3F4F6",
                      borderColor: "#D1D5DB",
                    },
                    "& .MuiAccordionSummary-expandIconWrapper": {
                      transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    },
                    "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                      transform: "rotate(180deg)",
                    },
                    "& .MuiAccordionSummary-content": {
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      pr: 0.5,
                      gap: 0.5,
                      margin: 0,
                      "&.Mui-expanded": {
                        margin: 0,
                      },
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
                    <WorkspacesTwoTone sx={{ fontSize: 14, color: "rgb(44, 52, 123)", flexShrink: 0 }} />
                    <Typography sx={{ fontWeight: 600, color: "#374151", fontSize: 11, whiteSpace: "nowrap" }}>
                      Shared Uploaded Files
                    </Typography>
                    {(() => {
                      const totalTables = sharedTables.reduce((acc, db) => acc + (db.children?.length || 0), 0);
                      return totalTables > 0 ? (
                        <Box
                          component="span"
                          sx={{
                            px: 0.5,
                            py: 0.125,
                            borderRadius: "6px",
                            backgroundColor: "#E0E7FF",
                            fontSize: "8.5px",
                            fontWeight: 600,
                            color: "rgb(44, 52, 123)",
                            minWidth: "14px",
                            textAlign: "center",
                            lineHeight: 1.2,
                            flexShrink: 0,
                          }}
                        >
                          {totalTables}
                        </Box>
                      ) : null;
                    })()}
                  </Box>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    p: 0,
                    pt: 0.1,
                    pb: 0.1,
                    px: 0.5,
                    boxSizing: "border-box",
                    width: "100%",
                    maxWidth: "100%",
                  }}
                >
                  <Box
                    sx={{
                      maxHeight: {
                        xs: "calc(100vh - 220px)", 
                        sm: "calc(100vh - 190px)",
                        md: "calc(100vh - 175px)",
                        lg: "calc(100vh - 165px)",
                      },
                      overflowY: "auto",
                      pr: 0.5,
                    }}
                  >
                    {renderTreeList(sharedTables, loadingSharedTables, "No shared uploaded files", "shared")}
                  </Box>
                </AccordionDetails>
              </Accordion>
              </Box>
              </Box>
            </ScrollableContent>
          </>
        )}
      </SecondarySidebar>
    </SidebarContainer>
  );
};

export default Sidebar;
